const anchor = require("@coral-xyz/anchor");
const { PublicKey } = anchor.web3;
const BN = anchor.BN;
const js_sha3 = require("js-sha3");

/**
 * Creates a node in the merkle tree from index, account and amount
 * @param {number} index - The index of the node
 * @param {PublicKey} account - The public key of the account
 * @param {BN} amount - The amount as a BN
 * @returns {Buffer} The hashed node
 */
const toNode = (index, account, amount) => {
    const buf = Buffer.concat([
        new BN(index).toArrayLike(Buffer, "be", 8),
        account.toBuffer(),
        amount.toArrayLike(Buffer, "be", 8)
    ]);
    return Buffer.from(js_sha3.keccak256(buf), "hex");
};

const bufDedup = (elements) => {
    return elements.filter(function (el, idx) {
        var _a;
        return idx === 0 || !((_a = elements[idx - 1]) === null || _a === void 0 ? void 0 : _a.equals(el));
    });
}

const combinedHash = (first, second) => {
    if (!first) {
        return second;
    }
    if (!second) {
        return first;
    }
    return Buffer.from(js_sha3.keccak_256.digest(sortAndConcat(first, second)));
}

const sortAndConcat = (...args) => {
    // var args = [];
    // for (var _i = 0; _i < arguments.length; _i++) {
    //     args[_i] = arguments[_i];
    // }
    return Buffer.concat([...args].sort(Buffer.compare.bind(null)));
}

const getPairElement = (idx, layer) => {
    var pairIdx = idx % 2 === 0 ? idx + 1 : idx - 1;
    if (pairIdx < layer.length) {
        var pairEl = layer[pairIdx];
        return pairEl;
    }
    else {
        return null;
    }
}

const bufArrToHexArr = (arr) => {
    if (arr.some(function (el) { return !Buffer.isBuffer(el); })) {
        throw new Error("Array is not an array of buffers");
    }
    return arr.map(function (el) { return "0x" + el.toString("hex"); });
}

class BalanceTree {
    constructor(balances) {
        this.tree = new MerkleTree(balances.map(function (item, index) {
            var account = item.account, amount = item.amount;
            return toNode(index, account, amount);
        }));
    }

    verifyProof(index, account, amount, proof, root) {
        let pair = this.toNode(index, account, amount);
        for (let i = 0; i < proof.length; i++) {
            let item = proof[i];
            pair = MerkleTree.combinedHash(pair, item);
        }
        return pair.equals(root);
    }

    getHexRoot() {
        return this.tree.getHexRoot();
    }

    getHexProof(index,account,amount) {
        return this.tree.getHexProof(toNode(index, account, amount));
    }

    getRoot() {
        return this.tree.getRoot();
    }

    getProof(index,account,amount) {
        return this.tree.getProof(toNode(index,account,amount));
    }
}

class MerkleTree {
    constructor(elements) {
        this.elements = [...elements];
        this.elements = bufDedup(this.elements);
        this.bufferElementPositionIndex = this.elements.reduce(function (memo, el, index) {
            memo[el.toString("hex")] = index;
            return memo;
        }, {});
        this.layers = this.getLayers(this.elements);
    }

    getLayers(elements) {
        if (elements.length === 0) {
            throw new Error("empty tree");
        }
        let layers = [];
        layers.push(elements);
        while (layers[layers.length - 1].length > 1) {
            let nextLayerIndex = layers[layers.length - 1];
            layers.push(this.getNextLayer(nextLayerIndex));
        }
        return layers;
    }

    getNextLayer(elements) {
        return elements.reduce(function (layer, el, idx, arr) {
            if (idx % 2 === 0) {
                // Hash the current element with its pair element
                var pairEl = arr[idx + 1];
                layer.push(combinedHash(el, pairEl));
            }
            return layer;
        }, []);
    }

    getRoot() {
        return this.layers[this.layers.length - 1][0];
    }

    getHexRoot() {
        return this.getRoot().toString("hex");
    }

    getProof(el) {
        var initialIdx = this.bufferElementPositionIndex[el.toString("hex")];
        if (typeof initialIdx !== "number") {
            throw new Error("Element does not exist in Merkle tree");
        }
        var idx = initialIdx;
        return this.layers.reduce(function (proof, layer) {
            var pairElement = getPairElement(idx, layer);
            if (pairElement) {
                proof.push(pairElement);
            }
            idx = Math.floor(idx / 2);
            return proof;
        }, []);
    }

    getHexProof(el) {
        var proof = this.getProof(el);
        return bufArrToHexArr(proof);
    }
}

/**
 * Generates merkle tree data from a JSON file
 * @returns {Object} Generated merkle tree data
 */
function generateMerkle(inputJson) {
    try {
        console.log(inputJson)
        const amounts_json = inputJson;

        // Create balance map
        const balanceMap = amounts_json.reduce((acc, { wallet, amount }) => {
            acc[wallet] = new BN(amount.toString());
            return acc;
        }, {});

        const sortedAddresses = Object.keys(balanceMap).sort();

        // Create merkle tree
        const tree = new BalanceTree(
            sortedAddresses.map(wallet => ({
                account: new PublicKey(wallet),
                amount: balanceMap[wallet]
            }))
        );

        // Generate claims
        const claimsRaw = sortedAddresses.reduce((memo, wallet, index) => {
            const amount = balanceMap[wallet];
            memo[wallet] = {
                index,
                amount,
                proof: tree.getProof(index, new PublicKey(wallet), amount)
            };
            return memo;
        }, {});

        const claims = sortedAddresses.reduce((memo, wallet, index) => {
            const amount = balanceMap[wallet];
            memo[wallet] = {
                index,
                amount: amount.toString(),
                proof: tree.getProof(index, new PublicKey(wallet), amount)
                    .map(proof => proof.toString("hex"))
            };
            return memo;
        }, {});

        // Calculate total tokens
        const tokenTotal = sortedAddresses.reduce(
            (memo, key) => memo.add(balanceMap[key]),
            new BN(0)
        );

        // Create output object
        const merkleDistributorInfo = {
            merkleRoot: tree.getRoot().toString("hex"),
            tokenTotal: tokenTotal.toString(),
            claims
        };

        return {
            root: tree.getRoot(),
            tokenTotal: tokenTotal.toString(),
            numNodes: Object.keys(claims).length,
            claims: claimsRaw,
            fileData: merkleDistributorInfo
        };
    } catch (error) {
        console.error("Error generating merkle tree:", error);
        throw error;
    }
}

module.exports = {
    generateMerkle,
    MerkleTree,
    BalanceTree
};